import React, { useState, useEffect } from 'react';
import API from '../../../API';
import NumberInput from './NumberInput';
import { T } from '../../../traks';
import { short_site_id, default_currency } from '../../../Site';
import { Row, Col, Button, Table } from 'reactstrap';
import { DatePickerLayout } from './DatePicker';
import { calculatePrice, determineExpirationDate } from './OrderUtils';
import { formatWithSeparators } from '../../../misc';
import OrderFooter from '../Globals/OrderFooter';
import { UpgradeGameSelectHandler } from './UpgradeGameSelect'
import { formatDate } from '../Globals/GlobalUtils';

export const OrderOverview = ({ cart, setCart, game, setGame, setView, vatRate, preDefinedMonths, render_static }) => {
  const [minimumOfKeys, setMinimumOfKeys] = useState(1);
  const [numberOfKeys, setNumberOfKeys] = useState(1);
  const [minimumOfMonths, setMinimumOfMonths] = useState(1);
  const [numberOfMonths, setNumberOfMonths] = useState(preDefinedMonths || 1);
  const [gameTitle, setGameTitle] = useState(null);
  const [prices, setPrices] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateError, setDateError] = useState(null);

  const subject = <T>Special offer for large-scale Aktiedyste</T>

  useEffect(() => {
    API.get('/z/premium_prices', { site_id: short_site_id }, (response, status) => {
      if (status >= 200 && status < 300) {
        setPrices(response.prices);
        setDiscounts(response.discounts);
        setCart({
          ...cart,
          prices: response.base_price,
          discounts: response.discounts,
        });
      }
    });
  }, []);

  const { total, totalNoVat, discountedTotal, discount } = calculatePrice(
    prices,
    discounts,
    numberOfKeys,
    numberOfMonths,
    vatRate
  );

  const handleAddToCart = () => {
    if (!selectedStartDate || selectedStartDate.trim() === "") {
      setDateError("* Please select a start date");
      return;
    } else {
      setDateError(null);
    }
    
    const expirationDate = determineExpirationDate(selectedStartDate, numberOfMonths);
    
    if (cart.lines && cart.lines.length > 0) {
      cart.lines[0] = {
        keys: numberOfKeys,
        months: numberOfMonths,
        discountedTotal: total,
        discountedTotalNoVat: totalNoVat,
        startDate: selectedStartDate,
        expirationDate: new Date(expirationDate).toISOString().split('T')[0],
        game: gameTitle

      };
    } else {
      cart.lines = [
        {
          keys: numberOfKeys,
          months: numberOfMonths,
          discountedTotal: total,
          discountedTotalNoVat: totalNoVat,
          startDate: selectedStartDate,
          expirationDate: new Date(expirationDate).toISOString().split('T')[0],
          game: gameTitle
        },
      ];
    }
    setCart({ ...cart });
    setView('cart');
  };


  useEffect(() => {
    if (cart.lines && cart.lines.length > 0) {
      setNumberOfKeys(cart.lines[0].keys);
      setNumberOfMonths(cart.lines[0].months);
      if (cart.lines[0].startDate) {
        setSelectedStartDate(cart.lines[0].startDate);
      }
    }
  }, [cart]);


  return (
    <div id="cart-order" className="p-4">
      <div className="cart-order-inner pb-5">
        <NumberInput
          label={<T>Number of participants</T>}
          imgSrc='https://static.aktiedysten.dk/gfx/user-icon-green-2023-0.svg'
          value={numberOfKeys}
          min={minimumOfKeys}
          onIncrement={() => setNumberOfKeys((prev) => prev + 1)}
          onDecrement={() => setNumberOfKeys((prev) => {
            if (prev - 1 >= minimumOfKeys) {
                return Math.max(1, prev - 1)
            } else {
                return prev
            }
            })
          }
          onChange={(e) => setNumberOfKeys(Number(e.target.value))}
          containerClassName='mb-4'
        />

        <NumberInput
          label={<T>Number of months</T>}
          imgSrc='/gfx/calendar-color-0.svg'
          value={numberOfMonths}
          onIncrement={() => setNumberOfMonths((prev) => prev + 1)}
          onDecrement={() => setNumberOfMonths((prev) => {
            if (prev - 1 >= minimumOfMonths) {
                return Math.max(1, prev - 1)
            } else {
                return prev
            }
            })
          }
          onChange={(e) => setNumberOfMonths(Number(e.target.value))}
          min={minimumOfMonths}
          containerClassName='mb-4'
        />

        <DatePickerLayout 
            label={<T>Start date</T>} 
            imgSrc='/gfx/calendar-color-0.svg'
            selectedStartDate={selectedStartDate} 
            setSelectedStartDate={setSelectedStartDate}
            containerClassName="mb-4" 
        />

        <UpgradeGameSelectHandler
          label={<T>Upgrade an ongoing game</T>} 
          imgSrc='https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg'  
          game={game}
          setGame={setGame}
          setGameTitle={setGameTitle}
          setNumberOfKeys={setNumberOfKeys}
          setMinimumOfKeys={setMinimumOfKeys}
          setNumberOfMonths={setNumberOfMonths}
          setMinimumOfMonths={setMinimumOfMonths}
          containerClassName='mb-4'
        />

        {dateError && (
            <Row className="mb-3">
                <Col>
                <div style={{ color: 'red' }}>{dateError}</div>
                </Col>
            </Row>
        )}

        <Row className="mt-4">
          <Col md="12" className="text-center">
            <h5>
              <T>Total Price:</T> {formatWithSeparators(total)} {default_currency}
            </h5>
            {discount > 0 && (
              <React.Fragment>
            <Row>
                <Col>
                <span className="text-success">
                    <T>Discount:</T> {discount}%, <T>you save</T> {formatWithSeparators(discountedTotal)} {default_currency}
                </span>
                </Col>
            </Row>
            
            </React.Fragment>
            )}

            <Row>
              <Col>
                <span>
                  <T>Premium valid until</T>: {formatDate(determineExpirationDate(selectedStartDate, numberOfMonths))}
                </span>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className='mt-4'>
            <Col xs="12" lg="6">
                <Table className='table-responsive-sm table-borderless table-sm'>
                    <thead>
                        <tr>
                            <td>
                                <b><T>Period</T></b>
                            </td>
                            <td>
                                <b><T>Discount</T></b>
                            </td>
                            <td>
                                <b><T>Per user per month</T></b>
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><T>1 month</T></td>
                            <td>-</td>
                            <td>18 kr.</td>
                        </tr>
                        <tr>
                            <td><T>3 months</T></td>
                            <td className='premium-green'>33%</td>
                            <td>12 kr.</td>
                        </tr>
                        <tr>
                            <td><T>6 months</T></td>
                            <td className='premium-green'>50%</td>
                            <td>9 kr.</td>
                        </tr>
                        <tr>
                            <td><T>12 months</T></td>
                            <td className='premium-green'>66%</td>
                            <td>6 kr.</td>
                        </tr>
                    </tbody>
                </Table>
            </Col>
        </Row>

         <Row className='mt-4 mb-5 premium-green-container'>
            <Col sm="12">
                <Row>
                    <Col>
                        <h4><T>More than 100 participants?</T></h4>
                    </Col>
                </Row>
                <Row className='mt-2'>
                    <Col>
                    <span><T>Should you engage your entire school in one big game, or would you rather target your local community? There are many great reasons to create large-scale Aktiedyste—and right now we’re offering a special campaign price for the big games.</T></span>
                    </Col>
                </Row>
                <Row className='mt-3'>
                    <Col>
                    <span><T>More than 100 participants =</T></span><strong><T> 6 kr. per participant per month, regardless of the game’s length. Plus, you get 14 days free</T></strong><span><T> to ensure everyone signs up before kickoff.</T></span>
                    </Col>
                </Row>
                <Row className='mt-3'>
                    <Col>
                    <span><T>Click below and let’s find the perfect solution for you together :)</T></span>
                    </Col>
                </Row>
                <Row className='mt-3'>
                    <Col>
                        <Button className="ml-auto mb-auto mr-2 px-4 py-2 premium-rounded-corners ad_btn_purple" tag="a" href={`mailto:mira@aktiedysten.dk?subject=${render_static(subject)}`}><T>Contact us here</T></Button> 
                    </Col>
                </Row>
            </Col>
        </Row>
      </div>
      <OrderFooter
        total={total}
        totalNoVat={totalNoVat}
        onNext={handleAddToCart}
        nextLabel={cart.lines && cart.lines.length !== 0 ? <T>Update Cart</T> : <T>Add to Cart</T>}
        backLabel={null}
      />
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/c/PremiumKeys/Order/OrderOverview.js